/* eslint-disable react/jsx-props-no-spreading */
import { memo, useMemo, useState } from 'react';
import type { AllowedStatesOptions } from '@library/types/allowedStates';
import { Combobox } from '@library/components/selects';
import { Form } from '@library/components/forms/Form';
import { INPUT_DEFAULT_CLASSES } from '@library/components/inputs/Input';
import { Input } from '@library/components/inputs';
import { PlayerPackDateTimePicker } from '@bo-components/playerPacks';
import type { PlayerPackFormValues } from '@library/types/playerPacks/PlayerPackFormSchema';
import { PrizepicksSwitch } from '@library/components/switches';
import { type UseFormReturn } from 'react-hook-form';
import { cn } from '@library/component-utils/cn';

export interface PlayerPacksFormFieldsProps {
  formMethods: UseFormReturn<PlayerPackFormValues>;
  isReadOnly: boolean;
  allowedStates: Array<AllowedStatesOptions>;
}

export const PlayerPacksFormFields = memo(
  ({ formMethods, isReadOnly, allowedStates }: PlayerPacksFormFieldsProps) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const statesOptions = useMemo(() => {
      return allowedStates
        ?.map((state) => ({
          label: state.name,
          value: state.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [allowedStates]);

    const handleImageChange = (file?: File) => {
      if (file) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    };
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-4">
            <Form.Field
              control={formMethods.control}
              name="title"
              render={({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Title <span className="text-roses"> * </span>
                  </Form.Label>
                  <Form.Control>
                    <Input
                      className={cn(
                        INPUT_DEFAULT_CLASSES,
                        'focus:outline-hidden mt-2 h-9 text-sm focus:ring-0'
                      )}
                      placeholder="Player Pack Title"
                      {...field}
                    />
                  </Form.Control>
                  <Form.Message />
                </Form.Item>
              )}
            />
            <Form.Field
              control={formMethods.control}
              name="image"
              render={({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Image<span className="text-roses"> * </span>
                  </Form.Label>
                  <Form.Control>
                    <Input
                      type="file"
                      accept="image/*"
                      className={cn(
                        INPUT_DEFAULT_CLASSES,
                        'file:text-soClean-170 focus:outline-hidden mt-2 h-9 px-2 file:border-0 file:bg-transparent file:text-sm file:font-medium md:text-sm'
                      )}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file);
                        handleImageChange(file);
                      }}
                    />
                  </Form.Control>
                  <Form.Message />
                </Form.Item>
              )}
            />
          </div>
          <div className="flex h-full flex-1 flex-col content-stretch">
            <p className="text-sm">Preview:</p>
            <div className="border-soFresh-140 mt-2 min-h-[64px] min-w-[358px] rounded-md border object-cover">
              {previewUrl && (
                <img
                  src={previewUrl || ''}
                  alt="Preview"
                  className="size-full rounded-md object-cover"
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <Form.Field
            control={formMethods.control}
            name="placeByTime"
            render={({ field }) => (
              <Form.Item className="flex-1">
                <Form.Label>
                  Place by<span className="text-roses"> * </span>
                </Form.Label>
                <Form.Control>
                  <div className="mt-2 flex flex-col">
                    <PlayerPackDateTimePicker
                      handleChange={(date) =>
                        field.onChange(date?.toISO() ?? '')
                      }
                      time={String(field.value) ?? ''}
                      minDate={new Date()}
                      disabled={isReadOnly}
                    />
                  </div>
                </Form.Control>
                <Form.Message />
              </Form.Item>
            )}
          />
          <Form.Field
            control={formMethods.control}
            name="excludedStates"
            render={({ field }) => (
              <Form.Item className="flex-1">
                <Form.Label>Excluded states</Form.Label>
                <Form.Control>
                  <div className="mt-2 flex flex-col">
                    <Combobox
                      closeMenuOnSelect
                      options={statesOptions}
                      isMulti
                      id="excludedStates"
                      value={statesOptions?.filter((option) =>
                        field.value?.includes(option.value)
                      )}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions?.map((opt) => opt.value);
                        field.onChange(values);
                      }}
                    />
                  </div>
                </Form.Control>
                <Form.Message />
              </Form.Item>
            )}
          />
          <Form.Field
            control={formMethods.control}
            name="canFade"
            render={({ field }) => (
              <Form.Item>
                <Form.Label>Can fade</Form.Label>
                <Form.Control>
                  <div className="mt-3 flex flex-col items-center">
                    <PrizepicksSwitch
                      active={Boolean(field.value)}
                      handleChange={field.onChange}
                    />
                  </div>
                </Form.Control>
                <Form.Message />
              </Form.Item>
            )}
          />
        </div>
      </div>
    );
  }
);
